import React, { useState } from 'react';
import { RecordItem } from '../../models/RecordItem';
import { Button, Input } from '../ui';

type Step = 'send_code' | 'enter_otp';

interface DeleteRecordModalProps {
  isOpen: boolean;
  record: RecordItem | null;
  userEmail: string;
  creatorId: number | undefined;
  onClose: () => void;
  onSendCode: (email: string) => Promise<{ success: boolean; error?: string }>;
  onConfirmDelete: (
    recordId: number,
    creatorId: number,
    verificationCode: string
  ) => Promise<{ success: boolean; error?: string }>;
  isDeleting?: boolean;
}

const DeleteRecordModal: React.FC<DeleteRecordModalProps> = ({
  isOpen,
  record,
  userEmail,
  creatorId,
  onClose,
  onSendCode,
  onConfirmDelete,
  isDeleting = false,
}) => {
  const [step, setStep] = useState<Step>('send_code');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [otp, setOtp] = useState('');
  const [sendCodeError, setSendCodeError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleClose = () => {
    setStep('send_code');
    setOtp('');
    setSendCodeError(null);
    onClose();
  };

  const handleSendCode = async () => {
    if (!userEmail?.trim()) {
      setSendCodeError('Email is required to send verification code.');
      return;
    }
    setIsSendingCode(true);
    setSendCodeError(null);
    try {
      const result = await onSendCode(userEmail.trim());
      if (result.success) {
        setStep('enter_otp');
      } else {
        setSendCodeError(result.error || 'Failed to send verification code.');
      }
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleDelete = async () => {
    if (!record?.id || creatorId == null || !otp.trim()) return;
    const result = await onConfirmDelete(record.id, creatorId, otp.trim());
    if (result.success) {
      handleClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-700 bg-opacity-50">
      <div className="bg-light rounded-lg shadow-lg max-w-md w-full mx-4 border-l-4 border-danger">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-2 text-danger">Delete Record</h3>

          {step === 'send_code' && (
            <>
              <p className="text-grey_dark mb-4">
                To delete &quot;{record?.record_name}&quot;, we need to verify your identity. We&apos;ll send a
                verification code to <strong>{userEmail || 'your email'}</strong>. Click below to send the code.
              </p>
              {sendCodeError && (
                <p className="text-danger text-sm mb-3">{sendCodeError}</p>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="bordered-primary" onClick={handleClose} className="min-w-[80px]">
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleSendCode}
                  disabled={isSendingCode}
                  className="min-w-[120px]"
                >
                  {isSendingCode ? 'Sending...' : 'Send code'}
                </Button>
              </div>
            </>
          )}

          {step === 'enter_otp' && (
            <>
              <p className="text-grey_dark mb-3">
                Enter the verification code sent to your email.
              </p>
              <Input
                type="text"
                attributes={{
                  placeholder: 'Enter verification code',
                  value: otp,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setOtp(e.target.value),
                  maxLength: 8,
                  'data-testid': 'delete-record-otp-input',
                }}
                className="mb-4"
              />
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="bordered-primary" onClick={handleClose} className="min-w-[80px]" disabled={isDeleting}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  disabled={!otp.trim() || isDeleting}
                  className="min-w-[80px]"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteRecordModal;
